import { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ICancelPartProTradeAward, IPartProTradeBidLot, PartProTradeCancelReason } from "../../models/partPro/IPartProTradeBids";
import {
  isValidCancelReason,
  normaliseCancelNote,
  PARTPRO_TRADE_CANCEL_NOTE_MAX_LENGTH,
  partProTradeCancelReasonLabels,
} from "../../utils/partProTradeCancel";
import PriceFormat from "../../utils/priceFormat";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

interface CancelTradeAwardModalProps {
  /** The accepted lot being cancelled; null closes the modal */
  lot: IPartProTradeBidLot | null;
  onClose: () => void;
  isLoading: boolean;
  onConfirm: (request: ICancelPartProTradeAward) => void;
}

interface FormFields {
  reason?: PartProTradeCancelReason;
  note: string;
}

// Cancelling an accepted bid tells the buyer why, so it always needs a reason; the note is extra
// detail for them. Shared by the pricing matrix and the Bids page.
export default function CancelTradeAwardModal({ lot, onClose, isLoading, onConfirm }: CancelTradeAwardModalProps) {
  const { t } = useTranslation("vehicles");

  const { register, setValue, watch, reset } = useForm<FormFields>({
    // No default: the buyer is told the reason, so it has to be a choice someone made
    defaultValues: { reason: undefined, note: "" },
  });

  useEffect(() => {
    if (!lot) reset({ reason: undefined, note: "" });
  }, [lot, reset]);

  const reason = watch("reason");
  const note = watch("note");
  const valid = isValidCancelReason(reason);

  const reasonOptions = (Object.values(PartProTradeCancelReason) as PartProTradeCancelReason[])
    .filter((value): value is PartProTradeCancelReason => typeof value === "number")
    .map((value) => ({ id: value, name: t(partProTradeCancelReasonLabels[value]) }));

  const buyer = lot?.award ? lot.award.accountName ?? lot.award.buyerCompanyName : "";

  return (
    <ConfirmModal
      open={!!lot}
      setOpen={(open: boolean) => !open && onClose()}
      onConfirm={() => {
        if (!isValidCancelReason(reason)) return;
        onConfirm({ reason, note: normaliseCancelNote(note) });
      }}
      isLoading={isLoading}
      confirmDisabled={!valid}
      title={t("Cancel the accepted bid on {{category}}?", { category: lot?.categoryName ?? "" })}
      message={t(
        "{{buyer}} will be emailed that their winning bid of {{amount}} has been cancelled, with your reason. The category is closed on PartPro for good and its price is cleared from the matrix. This can't be undone.",
        { buyer, amount: lot?.award ? PriceFormat(lot.award.amount) : "" }
      )}
      confirmButtonText={t("Cancel bid")}
    >
      <div className="mt-4 flex flex-col gap-y-3">
        <SelectInput
          label={t("Reason")}
          register={register}
          registerName="reason"
          options={reasonOptions}
          setValue={setValue}
          watch={watch}
          cols="w-full"
          placeholder={t("Select a reason")}
          rounded
        />

        <TextInput
          label={t("Note for the buyer (optional)")}
          register={register}
          registerName="note"
          richText
          rows={3}
          cols="w-full"
          maxLength={PARTPRO_TRADE_CANCEL_NOTE_MAX_LENGTH}
        />
      </div>
    </ConfirmModal>
  );
}
